import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {
  Button,
  createStyles,
  getStylesRef,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import ProcedureChipPopoverContents from 'procedures/chip-utils/ProcedureChipPopoverContents';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {getPowAgnosticPublishedFlowchartVersionFromProcedure} from 'procedures/utils/powAgnosticFlowchartHelpers';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {
  NODE_HOVER_BOX_SHADOW,
  STACKED_NODE_BOX_SHADOW,
  STACKED_NODE_HOVER_BOX_SHADOW,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {CenteredSpinner} from 'ui-library/Spinner';

import {ProcedureCardQuery, ProcedureCardQueryVariables} from './__generated/ProcedureCardQuery';

const DISABLED_UPDATE_TOOLTIP = 'Save or discard changes to your plan before updating';

interface ProcedureCardProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  onRemoveProcedure: () => Promise<void>;
  isEditable: boolean;
  hasUpdateAvailable: boolean;
  isUpdateDisabled: boolean;
  onOpenUpdateModal: () => void;
}

export const PROCEDURE_CARD_QUERY = gql`
  query ProcedureCardQuery($procedureId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        name
        templateCollection {
          id
          name
        }
        publishedProcedureFlowchartConfigVersionProxy {
          id
          nodes {
            id
          }
        }
        procedureFlowchart {
          id
          publishedFlowchartVersion {
            id
            nodes {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface PresentationalProcedureCardProps extends ProcedureCardProps {
  procedureName: string;
  templateCollectionName: string;
  hasMultipleUnitOps: boolean;
  /**
   * When true, the card can be dragged for procedure selection in the run plan design empty state.
   */
  isDraggable?: boolean;
  onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
  procedureUrl?: string;
}

export function PresentationalProcedureCard({
  procedureId,
  flowchartVersionId,
  procedureType,
  procedureName,
  templateCollectionName,
  onRemoveProcedure,
  isEditable,
  hasUpdateAvailable,
  isUpdateDisabled,
  onOpenUpdateModal,
  hasMultipleUnitOps,
  isDraggable,
  onDragStart,
  procedureUrl,
}: PresentationalProcedureCardProps): JSX.Element | null {
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const {classes, cx} = useProcedureCardStyles();

  const nameAndCollection = (
    <Stack spacing={2}>
      <Group spacing={8} noWrap={true}>
        {hasUpdateAvailable ? (
          <BenchlingIcon icon="exclamation" color={Color.RED_500} className={classes.alertIcon} />
        ) : (
          <BenchlingIcon icon={getProcedureIcon(procedureType)} className={classes.icon} />
        )}
        <Text className={cx(classes.procedureName, procedureUrl && classes.procedureLinkName)}>
          {procedureName}
        </Text>
      </Group>
      <Text className={classes.collectionName}>{templateCollectionName}</Text>
    </Stack>
  );

  const popoverContent = (): JSX.Element => (
    <ProcedureChipPopoverContents id={procedureId} flowchartVersionId={flowchartVersionId} />
  );

  const handleOpenUpdateModal = (): void => {
    if (!isUpdateDisabled) {
      onOpenUpdateModal();
    }
  };

  const handleProcedureClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    if (BenchlingUtil.isSimpleClick(event)) {
      event.preventDefault();
      navigateToURL(procedureUrl ?? '');
    }
  };

  const procedureDetails = procedureUrl ? (
    <div className={classes.procedureLinkContainer}>
      <a
        className={classes.procedureLink}
        aria-label={`${procedureName} ${templateCollectionName}`}
        href={procedureUrl}
        onClick={handleProcedureClick}
        target="_blank"
        rel="noreferrer noopener"
        {...getTestProps({component: 'ProcedureCard', element: 'procedureLink'})}
      />
      {nameAndCollection}
    </div>
  ) : (
    nameAndCollection
  );

  return (
    <Stack spacing={12} {...getTestProps({component: 'ProcedureCard', element: 'root'})}>
      <Paper
        withBorder={false}
        className={cx(
          classes.card,
          hasMultipleUnitOps && classes.stackedCard,
          isDraggable && classes.draggableCard,
          isDraggable && hasMultipleUnitOps && classes.draggableStackedCard
        )}
        draggable={isDraggable}
        onDragStart={isDraggable ? onDragStart : undefined}
        aria-label={isDraggable ? `Drag ${procedureName}` : undefined}
        {...(isDraggable
          ? getTestProps({component: 'ProcedureCard', element: 'dragHandle'})
          : getTestProps({component: 'ProcedureCard', element: 'card'}))}
      >
        <div className={classes.cardContent}>
          <Group position="apart" align="center" noWrap={true}>
            {isDraggable ? (
              <Popover
                interaction={FloatingInteraction.HOVER}
                delay={{open: 300, close: 250}}
                renderContent={popoverContent}
              >
                <div className={classes.popoverWrapper}>{procedureDetails}</div>
              </Popover>
            ) : (
              <div className={classes.popoverWrapper}>
                <Popover renderContent={popoverContent}>
                  <div
                    className={classes.popoverTrigger}
                    {...getTestProps({component: 'ProcedureCard', element: 'popoverTrigger'})}
                  >
                    {procedureDetails}
                  </div>
                </Popover>
              </div>
            )}
            {isEditable && (
              <UnstyledButton
                onClick={onRemoveProcedure}
                className={classes.trashButton}
                aria-label={`Remove ${procedureString}`}
                {...getTestProps({component: 'ProcedureCard', element: 'trashIconButton'})}
              >
                <BenchlingIcon icon="delete" className={classes.trashIcon} />
              </UnstyledButton>
            )}
          </Group>
        </div>
        {hasUpdateAvailable && (
          <div className={classes.updateButton}>
            <Tooltip
              label={DISABLED_UPDATE_TOOLTIP}
              disabled={!isUpdateDisabled}
              events={{hover: true, focus: true, touch: true}}
            >
              <Button
                variant="ghost"
                compact={true}
                aria-disabled={isUpdateDisabled || undefined}
                classNames={{
                  root: classes.updateButtonRoot,
                  label: cx(classes.updateButtonLabel, isUpdateDisabled && classes.updateButtonLabelDisabled),
                }}
                onClick={handleOpenUpdateModal}
                {...getTestProps({component: 'ProcedureCard', element: 'updateButton'})}
              >
                Update available
              </Button>
            </Tooltip>
          </div>
        )}
      </Paper>
    </Stack>
  );
}

export default function ProcedureCard({
  procedureId,
  flowchartVersionId,
  procedureType,
  studyType,
  onRemoveProcedure,
  isEditable,
  hasUpdateAvailable,
  isUpdateDisabled,
  onOpenUpdateModal,
}: ProcedureCardProps): JSX.Element | null {
  const result = useBenchlingQuery<ProcedureCardQuery, ProcedureCardQueryVariables>(PROCEDURE_CARD_QUERY, {
    variables: {procedureId},
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (
    result.type === QueryResultType.ERROR ||
    result.data.procedure == null ||
    isInaccessible(result.data.procedure)
  ) {
    return null;
  }

  const procedure = result.data.procedure;
  const flowchartNodes = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure)?.nodes ?? [];
  const hasMultipleUnitOps = flowchartNodes.length > 1;

  return (
    <PresentationalProcedureCard
      procedureId={procedureId}
      flowchartVersionId={flowchartVersionId}
      procedureType={procedureType}
      procedureName={procedure.name}
      templateCollectionName={procedure.templateCollection.name}
      studyType={studyType}
      onRemoveProcedure={onRemoveProcedure}
      isEditable={isEditable}
      hasUpdateAvailable={hasUpdateAvailable}
      isUpdateDisabled={isUpdateDisabled}
      onOpenUpdateModal={onOpenUpdateModal}
      hasMultipleUnitOps={hasMultipleUnitOps}
      procedureUrl={procedurePageUrl(procedureId, {procedureFlowchartVersionId: flowchartVersionId})}
    />
  );
}

export const useProcedureCardStyles = createStyles((theme) => ({
  card: {
    position: 'relative',
    zIndex: 1,
    borderColor: theme.colors.gray[3],
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
  },
  stackedCard: {
    boxShadow: STACKED_NODE_BOX_SHADOW,
  },
  draggableCard: {
    cursor: 'grab',
    transition: 'border-color 100ms ease, box-shadow 100ms ease',
    '&:hover': {
      borderColor: theme.colors.blue[5],
      // Not actually a flowchart node, but styled to look the same
      boxShadow: NODE_HOVER_BOX_SHADOW,
    },
    '&:active': {
      cursor: 'grabbing',
      borderColor: theme.colors.blue[5],
      boxShadow: NODE_HOVER_BOX_SHADOW,
    },
  },
  draggableStackedCard: {
    '&:hover': {
      boxShadow: STACKED_NODE_HOVER_BOX_SHADOW,
    },
    '&:active': {
      borderColor: theme.colors.blue[5],
      boxShadow: STACKED_NODE_HOVER_BOX_SHADOW,
    },
  },
  cardContent: {
    padding: 8,
  },
  icon: {
    color: theme.colors.gray[7],
    fontSize: 16,
    lineHeight: 1,
  },
  alertIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  procedureName: {
    color: Color.BLACK,
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: '16px',
  },
  procedureLinkName: {
    ref: getStylesRef('procedureLinkName'),
  },
  collectionName: {
    color: theme.colors.gray[6],
    fontSize: 11,
    lineHeight: '14px',
  },
  popoverWrapper: {
    flexBasis: '100%',
    minWidth: 0,
  },
  popoverTrigger: {
    width: '100%',
  },
  procedureLinkContainer: {
    position: 'relative',
    [`&:hover .${getStylesRef('procedureLinkName')}`]: {
      textDecoration: 'underline',
    },
  },
  procedureLink: {
    cursor: 'pointer',
    display: 'block',
    inset: 0,
    position: 'absolute',
    textDecoration: 'none',
    width: '100%',
    zIndex: 1,
  },
  trashButton: {
    color: theme.colors.gray[6],
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    flexShrink: 0,
  },
  trashIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  updateButton: {
    paddingTop: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    paddingLeft: 8,
    paddingRight: 8,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
  updateButtonRoot: {
    paddingLeft: 0,
  },
  updateButtonLabel: {
    color: theme.colors.blue[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '18px',
  },
  updateButtonLabelDisabled: {
    color: theme.colors.gray[4],
  },
}));

export type ProcedureCardClasses = ReturnType<typeof useProcedureCardStyles>['classes'];
